/**
 * AuthStep Component
 * Step 3: Authorize QBridge with JWT token
 *
 * Features:
 * - Single-click authorization
 * - Handshake animation during connection
 * - Auto-check if already authorized
 * - Q.AI Coach branded sign-in experience
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import SecurityIcon from '@mui/icons-material/Security';
import ErrorIcon from '@mui/icons-material/Error';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { authorizeQBridge, checkQBridgeAuth } from '../../../../services/qbridge.service';
import { toast } from 'react-toastify';

const AuthStep = ({
    getToken,
    mt5Account,
    mt5Broker,
    onComplete,
    onBack,
    colors
}) => {
    const navigate = useNavigate();
    const [authStatus, setAuthStatus] = useState('idle'); // idle, checking, authorizing, success, error, no_token
    const [error, setError] = useState(null);

    // Check if already authorized on mount
    useEffect(() => {
        const checkExistingAuth = async () => {
            // First check if user has a token (logged in to Q.AI)
            const token = getToken();
            if (!token) {
                setAuthStatus('no_token');
                setError('Please sign in to link your account.');
                return;
            }

            setAuthStatus('checking');
            const result = await checkQBridgeAuth();

            if (result.authorized) {
                setAuthStatus('success');
                // Auto-advance after brief delay
                setTimeout(() => {
                    onComplete(result.email || null);
                }, 1000);
            } else {
                setAuthStatus('idle');
            }
        };

        checkExistingAuth();
    }, [onComplete, getToken]);

    // Handle sign in redirect
    const handleSignIn = () => {
        // Navigate to Q.AI login page
        navigate('/q-ai/login', { state: { returnTo: '/qbridge/setup' } });
    };

    // Handle authorize click
    const handleAuthorize = async () => {
        setAuthStatus('authorizing');
        setError(null);

        const token = getToken();
        if (!token) {
            setError('Please sign in to continue.');
            setAuthStatus('no_token');
            return;
        }

        const result = await authorizeQBridge(token);

        if (result.success) {
            setAuthStatus('success');
            toast.success('Account linked successfully!');
            // Auto-advance after animation
            setTimeout(() => {
                onComplete(result.email || null);
            }, 1500);
        } else {
            setError(result.error || 'Authorization failed. Please try again.');
            setAuthStatus('error');
            toast.error(result.error || 'Authorization failed');
        }
    };

    // Handle retry
    const handleRetry = () => {
        setAuthStatus('idle');
        setError(null);
    };

    // Render based on status
    if (authStatus === 'checking') {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress size={48} sx={{ color: colors.primary, mb: 2 }} />
                <Typography sx={{ color: colors.textSecondary, fontSize: '0.9375rem' }}>
                    Checking authorization...
                </Typography>
            </Box>
        );
    }

    if (authStatus === 'success') {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                {/* Success Animation */}
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${colors.primary} 0%, #22C55E 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px',
                        boxShadow: `0 0 40px ${colors.primaryLight}`,
                        animation: 'pulse 1.5s ease-out'
                    }}
                >
                    <CheckCircleIcon sx={{ color: '#FFFFFF', fontSize: '2.5rem' }} />
                </Box>

                <Typography
                    sx={{
                        color: colors.text,
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        mb: 1
                    }}
                >
                    Account Linked!
                </Typography>

                <Typography
                    sx={{
                        color: colors.textMuted,
                        fontSize: '0.9375rem',
                        mb: 1,
                        lineHeight: 1.5
                    }}
                >
                    QBridge is now connected to your Q AI account
                </Typography>

                {mt5Account && (
                    <Typography
                        sx={{
                            color: colors.primary,
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            lineHeight: 1.4
                        }}
                    >
                        MT5: {mt5Account} @ {mt5Broker}
                    </Typography>
                )}
            </Box>
        );
    }

    // No token state - user needs to sign in to Q.AI Coach
    if (authStatus === 'no_token') {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                {/* Q.AI Coach Logo */}
                <Box
                    sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #27B56D 0%, #22C55E 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                        boxShadow: '0 4px 20px rgba(39, 181, 109, 0.3)'
                    }}
                >
                    <Typography
                        sx={{
                            color: '#FFFFFF',
                            fontWeight: 800,
                            fontSize: '2rem',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                        }}
                    >
                        Q
                    </Typography>
                </Box>

                {/* Brand Title */}
                <Typography
                    sx={{
                        color: colors.text,
                        fontSize: '1.25rem',
                        fontWeight: 800,
                        mb: 0.5,
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}
                >
                    Q<span style={{ color: '#27B56D' }}>.ai</span> Coach
                </Typography>

                <Typography
                    sx={{
                        color: colors.textMuted,
                        fontSize: '0.9375rem',
                        mb: 2.5,
                        fontStyle: 'italic',
                        lineHeight: 1.5
                    }}
                >
                    Your AI Trading Coach
                </Typography>

                {/* Sign In Message */}
                <Typography
                    sx={{
                        color: colors.textSecondary,
                        fontSize: '0.9375rem',
                        mb: 3,
                        px: 2,
                        lineHeight: 1.6
                    }}
                >
                    Sign in to connect your MT5 account and unlock personalized trading insights
                </Typography>

                {/* Features List */}
                <Box sx={{ mb: 3, px: 2 }}>
                    {[
                        'Auto-import your MT5 trades',
                        'AI-powered trade analysis',
                        '24/7 personalized guidance'
                    ].map((feature, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                                justifyContent: 'center'
                            }}
                        >
                            <AutoAwesomeIcon sx={{ color: '#27B56D', fontSize: '1rem' }} />
                            <Typography sx={{ color: colors.textMuted, fontSize: '0.9375rem', lineHeight: 1.5 }}>
                                {feature}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Sign In Button */}
                <Button
                    onClick={handleSignIn}
                    sx={{
                        background: 'linear-gradient(135deg, #27B56D 0%, #22C55E 100%)',
                        color: '#FFFFFF',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        padding: '14px 40px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 16px rgba(39, 181, 109, 0.3)',
                        mb: 2,
                        '&:hover': {
                            boxShadow: '0 6px 24px rgba(39, 181, 109, 0.4)',
                            background: 'linear-gradient(135deg, #22A55E 0%, #16A34A 100%)'
                        }
                    }}
                >
                    Sign In to Q.ai
                </Button>

                {/* Back Button */}
                <Button
                    onClick={onBack}
                    sx={{
                        color: colors.textMuted,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        '&:hover': { color: colors.textSecondary }
                    }}
                >
                    ← Go Back
                </Button>
            </Box>
        );
    }

    if (authStatus === 'error') {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                {/* Error Icon */}
                <Box
                    sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: colors.errorLight,
                        border: `2px solid ${colors.error}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                    }}
                >
                    <ErrorIcon sx={{ color: colors.error, fontSize: '2rem' }} />
                </Box>

                <Typography
                    sx={{
                        color: colors.text,
                        fontSize: '1.125rem',
                        fontWeight: 700,
                        mb: 1
                    }}
                >
                    Authorization Failed
                </Typography>

                <Typography
                    sx={{
                        color: colors.error,
                        fontSize: '0.9375rem',
                        mb: 3,
                        px: 2,
                        lineHeight: 1.5
                    }}
                >
                    {error}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                        onClick={handleRetry}
                        sx={{
                            background: colors.surface,
                            border: `1px solid ${colors.border}`,
                            color: colors.text,
                            textTransform: 'none',
                            fontWeight: 600,
                            padding: '10px 24px',
                            borderRadius: '10px',
                            '&:hover': { background: colors.surfaceHover }
                        }}
                    >
                        Try Again
                    </Button>
                </Box>

                <Button
                    onClick={onBack}
                    sx={{
                        color: colors.textMuted,
                        textTransform: 'none',
                        mt: 2,
                        '&:hover': { color: colors.textSecondary }
                    }}
                >
                    ← Go Back
                </Button>
            </Box>
        );
    }

    // Default: Idle state - show authorize button
    return (
        <Box>
            {/* Heading */}
            <Typography
                sx={{
                    color: colors.text,
                    fontSize: '1rem',
                    fontWeight: 600,
                    mb: 0.5
                }}
            >
                Step 3: Link Your Account
            </Typography>
            <Typography
                sx={{
                    color: colors.textMuted,
                    fontSize: '0.9375rem',
                    mb: 3,
                    lineHeight: 1.5
                }}
            >
                Securely connect QBridge to your Q AI account
            </Typography>

            {/* Account Info Card */}
            {mt5Account && (
                <Box
                    sx={{
                        background: colors.surface,
                        border: `1px solid ${colors.borderLight}`,
                        borderRadius: '12px',
                        padding: '16px',
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            background: colors.primaryLight,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <LinkIcon sx={{ color: colors.primary, fontSize: '1.5rem' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ color: colors.text, fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 }}>
                            {mt5Account}
                        </Typography>
                        <Typography sx={{ color: colors.textMuted, fontSize: '0.875rem', lineHeight: 1.5 }}>
                            {mt5Broker}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Authorize Button */}
            <Button
                onClick={handleAuthorize}
                disabled={authStatus === 'authorizing'}
                fullWidth
                startIcon={
                    authStatus === 'authorizing'
                        ? <CircularProgress size={20} color="inherit" />
                        : <LinkIcon />
                }
                sx={{
                    background: `linear-gradient(135deg, ${colors.primary} 0%, #22C55E 100%)`,
                    color: '#FFFFFF',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    padding: '16px 24px',
                    borderRadius: '12px',
                    boxShadow: `0 4px 16px ${colors.primaryLight}`,
                    mb: 3,
                    '&:hover': {
                        boxShadow: `0 6px 24px rgba(39, 181, 109, 0.4)`
                    },
                    '&.Mui-disabled': {
                        background: 'rgba(39, 181, 109, 0.3)',
                        color: 'rgba(255, 255, 255, 0.7)'
                    }
                }}
            >
                {authStatus === 'authorizing' ? 'Connecting...' : 'Authorize QBridge'}
            </Button>

            {/* Security Note */}
            <Box
                sx={{
                    background: colors.primaryLight,
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: '10px',
                    padding: '12px 16px',
                    mb: 3
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SecurityIcon sx={{ color: colors.primary, fontSize: '1rem' }} />
                    <Typography
                        sx={{
                            color: colors.textSecondary,
                            fontSize: '0.9375rem',
                            lineHeight: 1.5
                        }}
                    >
                        Your credentials are stored securely on your computer
                    </Typography>
                </Box>
            </Box>

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                    onClick={onBack}
                    sx={{
                        color: colors.textSecondary,
                        textTransform: 'none',
                        '&:hover': { backgroundColor: colors.surfaceHover }
                    }}
                >
                    ← Back
                </Button>
            </Box>
        </Box>
    );
};

export default AuthStep;
